import Head from 'next/head';
import type { GetStaticProps } from 'next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useReactiveVar } from '@apollo/client';

import {
  ICountryList,
  TfrequenciesV2,
  allFiltersType,
  getCategorizedProductsType,
  plansType,
} from '@utils/types';
import OrderStepTwo from '@containers/OrderStepTwo';
import { redirectIndiaModalOnesOpenVar, utilsStateVar } from 'store/utils';
import { AppRoutes } from '@utils/appRoutes';
import PageLoader from '@components/PageLoader';
import { fetchServiceableCountries } from '@utils/fetchApi/fetchServiceableCountries';
import { fetchPlanAndFreq } from '@utils/fetchApi/fetchPlanAndFreq';
import { fetchCategorizedProduct } from '@utils/fetchApi/fetchCategorizedProduct';
import CombinedOrderStepOneAndTwo from '@containers/CombinedOrderStepOneAndTwo';
import NewStepTwo from '@containers/NewStepTwo';
import { STEP2_REDESIGN_ACTIVE } from '@utils/abTest';
import useStep2RedesignVariant from '@utils/useStep2RedesignVariant';
import { STEP2_REDESIGN_GUARD_SCRIPT } from '@utils/step2RedesignGuard';

// Variant B of step2_redesign_v1: loaded only when the visitor is assigned B,
// so control traffic never downloads the redesign bundle.
const NewStepTwoV2 = dynamic(() => import('@containers/NewStepTwoV2'), {
  ssr: false,
});

interface OrderFlowStepTwoProps {
  servicableCountriesResponse: {
    data: {
      getServiceableCountries: ICountryList[];
    };
  };
  plansAndFrequencies: {
    data: {
      getPlansAndFrequencies: {
        frequencies: string[];
        plans: plansType[];
        frequenciesV2: TfrequenciesV2[];
      };
    };
  };
  categorizedResponse: {
    data: {
      getCategorizedProducts: {
        categories: getCategorizedProductsType[];
        all_filters: allFiltersType[];
        allVariantMapping: any;
      };
    };
  };
  countryCode: string;
}

const OrderFlowStepTwo = ({
  servicableCountriesResponse,
  plansAndFrequencies,
  categorizedResponse,
  countryCode,
}: OrderFlowStepTwoProps) => {
  const { getServiceableCountries } = servicableCountriesResponse?.data || {};
  const { plans, frequencies, frequenciesV2 } =
    plansAndFrequencies?.data?.getPlansAndFrequencies || {};
  const { getCategorizedProducts } = categorizedResponse?.data || {};
  const { categories, all_filters, allVariantMapping } =
    getCategorizedProducts || {};

  const router = useRouter();

  // step2_redesign_v1 gate: 'B' -> redesigned page; 'A' or null (unresolved/
  // unassigned) -> control. Assignment + impressions happen inside the hook.
  const step2Variant = useStep2RedesignVariant(countryCode);

  const { isDirectRoutingValid } = useReactiveVar(utilsStateVar);
  const redirectIndiaModalOnesOpen = useReactiveVar(
    redirectIndiaModalOnesOpenVar
  );

  // useEffect(() => {
  //   if (!isDirectRoutingValid) {
  //     //router.push(`/${countryCode}${AppRoutes.ORDER_STEP_ONE}`);
  //     router.push(`${AppRoutes.HOME_ROUTE}`);
  //   }
  // }, [isDirectRoutingValid]);

  // if (!isDirectRoutingValid) {
  //   return <PageLoader />;
  // }

  const handleChange = () => {
    router.push(AppRoutes.HOME_ROUTE);
  };

  // for clear back stack
  useEffect(() => {
    if (redirectIndiaModalOnesOpen) {
      router.beforePopState(() => {
        return false;
      });
      window.addEventListener('popstate', handleChange);

      return () => {
        router.beforePopState(() => {
          return true;
        });
        window.removeEventListener('popstate', handleChange);
      };
    }
  }, [redirectIndiaModalOnesOpen]);

  return (
    <>
      <Head>
        <title>
          Menu | Taste of India - Delhi, Mumbai, Chennai | The Cumin Club
        </title>
      </Head>
      {/* Pre-hydration guard: hides control for likely-B visitors until the
          variant resolves (removed by useStep2RedesignVariant). Rendered only
          while the test is active, so the kill switch also removes it. */}
      {STEP2_REDESIGN_ACTIVE && (
        // eslint-disable-next-line react/no-danger
        <script
          dangerouslySetInnerHTML={{ __html: STEP2_REDESIGN_GUARD_SCRIPT }}
        />
      )}
      {step2Variant === 'B' ? (
        <NewStepTwoV2
          getServiceableCountries={getServiceableCountries}
          plans={plans}
          frequencies={frequencies}
          frequenciesV2={frequenciesV2}
          categories={categories}
          getAllFilters={all_filters}
          allVariantMapping={allVariantMapping}
          countryCode={countryCode}
        />
      ) : (
        <div id="s2-control-root">
          <NewStepTwo
            getServiceableCountries={getServiceableCountries}
            plans={plans}
            frequencies={frequencies}
            frequenciesV2={frequenciesV2}
            categories={categories}
            getAllFilters={all_filters}
            allVariantMapping={allVariantMapping}
            countryCode={countryCode}
            subscribeSwitchLayout
          />
        </div>
      )}
    </>
  );
};

export default OrderFlowStepTwo;

export const getStaticProps: GetStaticProps = async () => {
  const countryCode = 'usa';

  const [
    servicableCountriesResponse,
    plansAndFrequencies,
    categorizedResponse,
  ]: any = await Promise.all([
    fetchServiceableCountries(),
    fetchPlanAndFreq({ countryCode }),
    fetchCategorizedProduct({ countryCode }),
  ]);

  // Pass data to the page via props
  return {
    props: {
      servicableCountriesResponse,
      plansAndFrequencies,
      categorizedResponse,
      countryCode,
    },
  };
};
